import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Award, Loader2, Wand2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CertRow } from "@/components/cert/CertRow";

export default function Certificates() {
  const { user } = useAuth();
  const [data, setData] = useState({ members: [], settings: {}, has_bg: false });
  const [sig, setSig] = useState({ signatory_name: "", signatory_title: "" });
  const [busy, setBusy] = useState("");
  const [pv, setPv] = useState("");

  const loadPreview = useCallback(() => api.get("/certificates-preview.png", { responseType: "blob" }).then((r) => setPv(URL.createObjectURL(r.data))).catch(() => {}), []);
  const load = useCallback(() => api.get("/certificates").then((r) => { setData(r.data); setSig({ signatory_name: r.data.settings.signatory_name || "", signatory_title: r.data.settings.signatory_title || "" }); }).catch(() => {}), []);
  useEffect(() => { load(); loadPreview(); }, [load, loadPreview]);
  if (user?.role !== "owner") { return <Navigate to="/inbox" replace />; }

  const act = async (k, fn, msg) => { setBusy(k); try { await fn(); toast.success(msg); load(); loadPreview(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } setBusy(""); };

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="certificates-page">
      <div>
        <p className="eyebrow text-gold/80">Company only</p>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold flex items-center gap-3"><Award className="w-8 h-8 text-gold" /> Franchise Certificates</h1>
        <p className="text-sm text-emerald-100/60 mt-1">Issue a certificate for each member with name, address, member ID, joining date and franchise area. Signed and sealed by the company.</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="glass border-gold/20 p-4 space-y-3" data-testid="cert-settings-card">
          <p className="font-serif font-semibold text-gold">Signature & design</p>
          <Input value={sig.signatory_name} onChange={(e) => setSig({ ...sig, signatory_name: e.target.value })} placeholder="Name to sign with, e.g. Ruhul Amin" data-testid="cert-sig-name" className="bg-card/60" />
          <Input value={sig.signatory_title} onChange={(e) => setSig({ ...sig, signatory_title: e.target.value })} placeholder="Title, e.g. Managing Director, Bengal Umrah House" data-testid="cert-sig-title" className="bg-card/60" />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => act("sig", () => api.post("/certificates-settings", sig), "Signature saved")} disabled={!!busy} data-testid="cert-sig-save" className="bg-gold text-[#0a2e22] hover:bg-gold/90">Save signature</Button>
            <Button size="sm" variant="outline" onClick={() => act("bg", () => api.post("/certificates-background", {}, { timeout: 180000 }), "New certificate design ready")} disabled={!!busy} data-testid="cert-bg-create" className="border-gold/40 text-gold gap-1.5">
              {busy === "bg" ? <><Loader2 className="w-4 h-4 animate-spin" /> Designing… up to 1 min</> : <><Wand2 className="w-4 h-4" /> Create design with AI</>}
            </Button>
            {data.has_bg && <Button size="sm" variant="outline" onClick={() => act("clr", () => api.delete("/certificates-background"), "Back to the saved Bengal Umrah House design")} disabled={!!busy} data-testid="cert-bg-clear" className="gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Use saved design</Button>}
          </div>
          <p className="text-[11px] text-emerald-100/50">The signature is typed in a handwriting style and the seal is drawn by the system. Wording, names and numbers are printed exactly, never by AI.</p>
        </Card>
        <Card className="glass border-gold/20 p-3" data-testid="cert-preview-card">
          {pv ? <img src={pv} alt="Certificate preview" className="w-full rounded-md" data-testid="cert-preview-img" /> : <div className="aspect-[1.41] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>}
        </Card>
      </div>
      <Card className="glass border-gold/20 overflow-x-auto" data-testid="cert-members-card">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-emerald-100/50 border-b border-border/40">
            <th className="p-3">Member</th><th className="p-3">Address</th><th className="p-3">Franchise area</th><th className="p-3">Certificate</th>
          </tr></thead>
          <tbody>{data.members.map((m) => <CertRow key={m.id} m={m} onChanged={load} />)}</tbody>
        </table>
        {!data.members.length && <p className="p-6 text-center text-sm text-emerald-100/50">No members yet.</p>}
      </Card>
    </div>
  );
}
